import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faSpinner } from '@/icons';
import { useTheme } from '../contexts/ThemeContext';
import { Modal } from './Modal';
import { authApiService } from '../services/authApiService';

interface User {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    teamName: string;
    teamId: string | null;
    role: string;
    businessRole: string;
    title?: string;
}

type UserSearchMode = 'default' | 'nameEmail';

interface UserSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectUser: (userId: string, userName?: string) => void;
    title?: string;
    loadUsersFn?: () => Promise<{ success: boolean; users?: User[]; data?: { users?: User[] } }>;
    /** When nameEmail, search matches first name, last name, or email only (admin log filters). */
    searchMode?: UserSearchMode;
}

export const UserSelectionModal: React.FC<UserSelectionModalProps> = ({
    isOpen,
    onClose,
    onSelectUser,
    title = "Select User",
    loadUsersFn,
    searchMode = 'default',
}) => {
    const theme = useTheme();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadUsers();
        } else {
            // Reset state when modal closes
            setUsers([]);
            setSearchQuery('');
            setError(null);
        }
    }, [isOpen]);

    const loadUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            if (loadUsersFn) {
                // Use custom load function if provided
                const response = await loadUsersFn();
                const usersList = response.users || response.data?.users || [];
                if (response.success) {
                    setUsers(usersList);
                } else {
                    setError('Failed to load users');
                }
            } else {
                const response = await authApiService.getAllActiveUsers();
                if (response.success && response.data.users) {
                    setUsers(response.data.users);
                } else {
                    setError('Failed to load users');
                }
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) {
            return users;
        }

        const query = searchQuery.toLowerCase();
        if (searchMode === 'nameEmail') {
            return users.filter((user) => {
                const first = (user.firstName || '').toLowerCase();
                const last = (user.lastName || '').toLowerCase();
                const email = (user.email || '').toLowerCase();
                const full = `${first} ${last}`.trim();
                return (
                    email.includes(query) ||
                    first.includes(query) ||
                    last.includes(query) ||
                    full.includes(query)
                );
            });
        }
        return users.filter(user =>
            user.email.toLowerCase().includes(query) ||
            user.firstName.toLowerCase().includes(query) ||
            user.lastName.toLowerCase().includes(query) ||
            user.fullName.toLowerCase().includes(query) ||
            user.teamName.toLowerCase().includes(query)
        );
    }, [users, searchQuery, searchMode]);

    const handleUserClick = (userId: string) => {
        const user = users.find(u => u.userId === userId);
        const userName = user ? `${user.firstName} ${user.lastName} (${user.email})` : undefined;
        onSelectUser(userId, userName);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            headerBackgroundColor={theme.colors.primary}
        >
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.md,
                minHeight: '400px',
                maxHeight: '70vh',
            }}>
                {/* Search Box */}
                <div style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <FontAwesomeIcon
                        icon={faSearch}
                        style={{
                            position: 'absolute',
                            left: theme.spacing.md,
                            color: theme.colors.text.secondary,
                            fontSize: theme.typography.fontSize.sm,
                        }}
                    />
                    <input
                        type="text"
                        placeholder={searchMode === 'nameEmail' ? 'Search by first name, last name, or email...' : 'Search by name, email, or team...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            width: '100%',
                            padding: `${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm} ${parseInt(theme.spacing.xl.replace('px', '')) * 2}px`,
                            border: `1px solid ${theme.colors.border.default}`,
                            borderRadius: theme.borderRadius.md,
                            fontSize: theme.typography.fontSize.sm,
                            backgroundColor: theme.colors.background,
                            color: theme.colors.text.primary,
                            outline: 'none',
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = theme.colors.primary;
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = theme.colors.border.default;
                        }}
                    />
                </div>

                {/* Loading State */}
                {loading && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: theme.spacing.xl,
                    }}>
                        <FontAwesomeIcon
                            icon={faSpinner}
                            spin
                            style={{
                                fontSize: theme.typography.fontSize.xl,
                                color: theme.colors.primary,
                            }}
                        />
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div style={{
                        padding: theme.spacing.md,
                        backgroundColor: theme.colors.status?.error || '#fee',
                        color: theme.colors.text.primary,
                        borderRadius: theme.borderRadius.md,
                        fontSize: theme.typography.fontSize.sm,
                    }}>
                        {error}
                    </div>
                )}

                {/* User List */}
                {!loading && !error && (
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        border: `1px solid ${theme.colors.border.default}`,
                        borderRadius: theme.borderRadius.md,
                        maxHeight: '50vh',
                    }}>
                        {filteredUsers.length === 0 ? (
                            <div style={{
                                padding: theme.spacing.xl,
                                textAlign: 'center',
                                color: theme.colors.text.secondary,
                                fontSize: theme.typography.fontSize.sm,
                            }}>
                                {searchQuery ? 'No users found matching your search' : 'No active users found'}
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}>
                                {filteredUsers.map((user) => (
                                    <button
                                        key={user.userId}
                                        type="button"
                                        onClick={() => handleUserClick(user.userId)}
                                        style={{
                                            padding: theme.spacing.md,
                                            border: 'none',
                                            borderBottom: `1px solid ${theme.colors.border.default}`,
                                            backgroundColor: 'transparent',
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            transition: 'background-color 0.2s ease',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: theme.spacing.xs,
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: `${parseInt(theme.spacing.xs.replace('px', '')) / 2}px`,
                                            }}>
                                                <div style={{
                                                    fontSize: theme.typography.fontSize.md,
                                                    fontWeight: theme.typography.fontWeight.medium,
                                                    color: theme.colors.text.primary,
                                                }}>
                                                    {user.fullName}
                                                </div>
                                                <div style={{
                                                    fontSize: theme.typography.fontSize.sm,
                                                    color: theme.colors.text.secondary,
                                                }}>
                                                    {user.email}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            gap: theme.spacing.md,
                                            fontSize: theme.typography.fontSize.xs,
                                            color: theme.colors.text.secondary,
                                        }}>
                                            <span>
                                                <strong>Team:</strong> {user.teamName}
                                            </span>
                                            {user.businessRole && (
                                                <span>
                                                    <strong>Role:</strong> {user.businessRole}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Results Count */}
                {!loading && !error && filteredUsers.length > 0 && (
                    <div style={{
                        fontSize: theme.typography.fontSize.xs,
                        color: theme.colors.text.secondary,
                        textAlign: 'right',
                    }}>
                        {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'} found
                    </div>
                )}
            </div>
        </Modal>
    );
};
